import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { type JSX, useState } from 'react';
import { supabase } from '../lib/supabase';
import { PageChrome } from '@/components/page-chrome';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardFoot, CardHead } from '@/components/ui/card';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { PageHead } from '@/components/ui/page-header';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface PasskeyRow {
  id: string;
  friendly_name?: string;
  created_at: string;
  last_used_at?: string;
}

const PASSKEYS_KEY = ['passkeys'];

function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
}

export function SettingsPage(): JSX.Element {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: PASSKEYS_KEY });
  };
  const fail = (e: unknown): void => {
    setError(e instanceof Error ? e.message : 'Opération échouée.');
  };

  const passkeys = useQuery<PasskeyRow[]>({
    queryKey: PASSKEYS_KEY,
    queryFn: async () => {
      const { data, error: err } = await supabase.auth.passkey.list();
      if (err) throw err;
      return data;
    },
  });

  const register = useMutation({
    mutationFn: async () => {
      setError(null);
      const { error: err } = await supabase.auth.registerPasskey();
      if (err) throw err;
    },
    onSuccess: invalidate,
    onError: fail,
  });

  const rename = useMutation({
    mutationFn: async (input: { passkeyId: string; friendlyName: string }) => {
      setError(null);
      const { error: err } = await supabase.auth.passkey.update(input);
      if (err) throw err;
    },
    onSuccess: invalidate,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: async (passkeyId: string) => {
      setError(null);
      const { error: err } = await supabase.auth.passkey.delete({ passkeyId });
      if (err) throw err;
    },
    onSuccess: invalidate,
    onError: fail,
  });

  const rows = passkeys.data ?? [];
  const addButton = (
    <Button
      type="button"
      onClick={() => {
        register.mutate();
      }}
      disabled={register.isPending}
    >
      <Plus aria-hidden="true" />
      {register.isPending ? 'Enregistrement…' : 'Ajouter une passkey'}
    </Button>
  );

  return (
    <>
      <PageChrome crumbs={[{ label: 'Paramètres' }]} actions={addButton} />
      <PageHead title="Paramètres" subtitle="Préférences du compte opérateur" />

      {error !== null && <Callout variant="destructive">{error}</Callout>}

      <Card>
        <CardHead
          title="Passkeys"
          description="Enregistrez une passkey sur cet appareil pour vous connecter sans e-mail (biométrie, code, ou clé de sécurité)."
          className="pb-4"
        />
        {passkeys.isLoading ? (
          <LoadingState className="border-t border-border" />
        ) : rows.length === 0 ? (
          <EmptyState className="border-t border-border" title="Aucune passkey enregistrée" />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead kind="mono">Créée le</TableHead>
                  <TableHead kind="mono">Dernière utilisation</TableHead>
                  <TableHead kind="act">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((pk) => {
                  const label = pk.friendly_name ?? 'passkey sans nom';
                  return (
                    <TableRow key={pk.id}>
                      <TableCell kind="name" className="w-full min-w-[200px]">
                        <Input
                          controlSize="sm"
                          aria-label={`Nom de la ${label}`}
                          defaultValue={pk.friendly_name ?? ''}
                          placeholder="Sans nom"
                          onChange={(e) => {
                            setDrafts((d) => ({ ...d, [pk.id]: e.target.value }));
                          }}
                          className="max-w-xs"
                        />
                      </TableCell>
                      <TableCell kind="mono">{formatDay(pk.created_at)}</TableCell>
                      <TableCell kind="mono">
                        {pk.last_used_at !== undefined ? (
                          formatDay(pk.last_used_at)
                        ) : (
                          <span className="font-sans text-muted-foreground">Jamais utilisée</span>
                        )}
                      </TableCell>
                      <TableCell kind="act">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            aria-label={`Renommer la ${label}`}
                            onClick={() => {
                              rename.mutate({
                                passkeyId: pk.id,
                                friendlyName: (drafts[pk.id] ?? pk.friendly_name ?? '').slice(
                                  0,
                                  120,
                                ),
                              });
                            }}
                            disabled={rename.isPending || drafts[pk.id] === undefined}
                          >
                            Renommer
                          </Button>
                          <Button
                            type="button"
                            variant="outline-destructive"
                            size="xs"
                            aria-label={`Supprimer la ${label}`}
                            onClick={() => {
                              remove.mutate(pk.id);
                            }}
                            disabled={remove.isPending}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <CardFoot
              start={`${String(rows.length)} passkey${rows.length > 1 ? 's' : ''} enregistrée${rows.length > 1 ? 's' : ''}`}
            />
          </>
        )}
      </Card>
    </>
  );
}
